/**
 * Which capabilities changed their outcome between one turn and the next.
 *
 * The outcome capabilities card shows where everything stands. This is the other
 * half: what the last message did to that standing. The turn card and the PDF
 * export both read from it.
 */
import { Outcome } from "@financial-advisory/data-access/api-types";
import type { TurnOutcomeState } from "../types";

export interface OutcomeChange {
  name: string;
  productTreeId: string;
  type: "situation" | "segment" | "suggestion";
  previousOutcome: Outcome | null;
  newOutcome: Outcome | null;
}

const isDecisive = (outcome: Outcome | null): boolean =>
  outcome === Outcome.TRUE || outcome === Outcome.FALSE;

/**
 * A change counts when the outcome moved and either end of the move was decided.
 */
export const calculateOutcomeChanges = (
  currentStates?: TurnOutcomeState[],
  previousStates?: TurnOutcomeState[]
): OutcomeChange[] => {
  if (!currentStates) return [];

  const previousOutcomes = new Map(
    (previousStates ?? []).map((state) => [state.productTreeId, state.outcome])
  );

  return currentStates
    .filter((state) => {
      const previousOutcome = previousOutcomes.get(state.productTreeId) ?? null;
      return (
        previousOutcome !== state.outcome &&
        (isDecisive(state.outcome) || isDecisive(previousOutcome))
      );
    })
    .map((state) => ({
      name: state.name,
      productTreeId: state.productTreeId,
      type: state.type,
      previousOutcome: previousOutcomes.get(state.productTreeId) ?? null,
      newOutcome: state.outcome,
    }));
};
